import { z } from "zod";
import { cn } from "@/shared/lib/utils";
import { AlertCircle, RotateCcw, Info } from "lucide-react";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const ErrorStateSchema = z.object({
  code: z.string(),
  message: z.string(),
  retryable: z.boolean(),
  suggestion: z.string().optional(),
});

type ErrorStateData = z.infer<typeof ErrorStateSchema>;

/* ────────────────────── Component ────────────────────── */

export function ErrorState({
  widget,
  onDeepLink,
  className,
}: AgentWidgetProps<ErrorStateData>) {
  const { code, message, retryable, suggestion } = widget.data;

  return (
    <div
      className={cn(
        "flex items-start gap-4 rounded-lg border border-red-200/60 bg-red-50/60 px-4 py-4 dark:border-red-800/40 dark:bg-red-950/20",
        className,
      )}
    >
      {/* Icon */}
      <div className="mt-0.5 shrink-0">
        <AlertCircle className="size-5 text-red-500" />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1 space-y-1.5">
        {/* Code badge + message */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">
            {code}
          </span>
          <p className="text-[13px] font-medium text-red-800 dark:text-red-300">
            {message}
          </p>
        </div>

        {/* Suggestion */}
        {suggestion && (
          <div className="flex items-start gap-1.5">
            <Info className="mt-0.5 size-3.5 shrink-0 text-red-400" />
            <p className="text-[12px] text-red-600 dark:text-red-400">
              {suggestion}
            </p>
          </div>
        )}

        {/* Retryable notice / deeplink */}
        <div className="flex items-center gap-3 pt-0.5">
          {retryable && (
            <span className="inline-flex items-center gap-1 text-[11px] text-red-500">
              <RotateCcw className="size-3" />
              재시도 가능
            </span>
          )}
          {widget.deepLink && onDeepLink && (
            <button
              onClick={() => onDeepLink(widget.deepLink!)}
              className="text-[12px] font-medium text-red-700 underline-offset-2 hover:underline dark:text-red-400"
            >
              도움말 보기 →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────── Skeleton (빈 div — 에러 상태에서는 불필요) ────────────────────── */

export function ErrorStateSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("h-16 rounded-xl bg-surface-sunken/30", className)} />
  );
}

/* ────────────────────── Widget Definition ────────────────────── */

const errorStateWidget = defineWidget<ErrorStateData>({
  type: WIDGET_TYPES.ERROR_STATE,
  label: "도구 실행 실패",
  schema: ErrorStateSchema,
  component: ErrorState,
  skeleton: ErrorStateSkeleton,
  defaultLayout: "full",
  maxHeight: 120,
  aiHint:
    "도구 실행 오류 피드백 (에러 코드, 메시지, 재시도 가능 여부, 해결 제안)",
});

registerWidget(errorStateWidget);
